import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '@spectrum-web-components/dialog/sp-dialog.js';
import '@spectrum-web-components/button/sp-button.js';
import '@spectrum-web-components/combobox/sp-combobox.js';
import '@spectrum-web-components/underlay/sp-underlay.js';
import type { TaskInteractionResponse } from '../../../api/types';
import type { WorkflowUserInputRequest } from '../dialogs/types.js';
import { dialogOverlayStyles } from './dialog-styles.js';
import {
  buildWorkflowUserInputSubmission,
  getWorkflowUserInputControls,
} from './user-input-controls.js';

type InteractionActionDetail = {
  taskId: number;
  response: TaskInteractionResponse;
  file?: File;
};

@customElement('kv-workflow-user-input-dialog')
export class KvWorkflowUserInputDialog extends LitElement {
  static styles = [
    dialogOverlayStyles,
    css`
      .content {
        background: var(--spectrum-gray-100);
        border-radius: 8px;
        padding: var(--gap-3);
        overflow: auto;
        font-family: var(--font-mono, monospace);
        font-size: 12px;
        line-height: 1.4;
        max-height: 320px;
      }
      sp-combobox {
        margin: var(--gap-2) 0;
        width: 100%;
      }
      .file-input {
        margin: var(--gap-2) 0;
      }
    `,
  ];

  @property({ attribute: false }) accessor request:
    | WorkflowUserInputRequest
    | undefined = undefined;

  @property({ type: Boolean }) accessor processing = false;
  @property() accessor processingAction: string | undefined = undefined;

  private format(value: unknown, emptyText?: string): string {
    if (typeof value === 'string') return value;
    if (value === undefined) return emptyText ?? 'No input data.';
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  private dispatchAction(detail: InteractionActionDetail) {
    if (!this.request) return;
    this.dispatchEvent(
      new CustomEvent('interaction-action', {
        detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  render() {
    if (!this.request) return nothing;
    const request = this.request;
    const interaction = request.interaction;
    const config = request.config ?? {};
    const title =
      (typeof config.title === 'string' && config.title.trim()) ||
      interaction?.title ||
      'User input required';
    const message =
      (typeof config.message === 'string' && config.message.trim()) ||
      interaction?.message ||
      '';

    const { controls, buttons } = getWorkflowUserInputControls(request);
    const hasSelectControl = controls.some((control) => control.type === 'select');
    const selectableOptionCount = controls
      .filter((control) => control.type === 'select')
      .reduce((count, control) => count + control.options.length, 0);
    const hasFileControl = controls.some((control) => control.type === 'file');

    return html`
      <sp-underlay open></sp-underlay>
      <div class="dialog-container">
        <sp-dialog open underlay size="m" role="dialog">
          <h2 slot="heading">${title}</h2>
          ${message ? html`<p>${message}</p>` : nothing}
          ${controls.map((control) => {
            if (control.type === 'select') {
              return html`<sp-combobox
                data-control-id=${control.controlId}
                label=${control.label}
                .value=${control.value}
                .options=${control.options}
                ?disabled=${this.processing || control.options.length === 0}
              ></sp-combobox>`;
            }
            if (control.type === 'file') {
              return html`<input
                class="file-input"
                data-control-id=${control.controlId}
                type="file"
                aria-label=${control.label}
                accept=${control.accept ?? ''}
                ?disabled=${this.processing}
              />`;
            }
            return html`<pre class="content">${this.format(control.value, control.emptyText)}</pre>`;
          })}
          ${buttons.map((button) => html`<sp-button
              slot="button"
              variant=${button.variant === 'secondary' ? 'secondary' : 'accent'}
              ?disabled=${this.processing ||
              (hasSelectControl && selectableOptionCount === 0 && !hasFileControl)}
              @click=${() => {
                const taskId = request.taskId;
                if (taskId === undefined) return;

                const submission = buildWorkflowUserInputSubmission({
                  request,
                  renderRoot: this.renderRoot,
                  actionName: button.name,
                });
                if (!submission) return;

                this.dispatchAction({
                  taskId,
                  response: submission.response,
                  file: submission.file,
                });
              }}
            >
              ${
                this.processing && this.processingAction === button.name
                  ? `${button.label}…`
                  : button.label
              }
            </sp-button>`)}
        </sp-dialog>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kv-workflow-user-input-dialog': KvWorkflowUserInputDialog;
  }
}
